import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { Suspense, useEffect, useState } from "react";
import CircularProgress from "@material-ui/core/CircularProgress";
import { Backdrop, Button, Card, CardContent, CardHeader, Fade, IconButton, Modal, Typography, Checkbox, useTheme } from "@material-ui/core";
import CloseIcon from "@material-ui/icons/Close";
import { CustomerGroupResource } from "@ploy-lib/rest-resources";
import { FormattedMessage } from "react-intl";
import { DployTable } from "@ploy-ui/core";
import { useFetcher } from "@rest-hooks/core";
import { useStatefulResource } from "@rest-hooks/legacy";
import { useModalStyles } from "./LimitCustomersSection";
import { DployFormControl } from "@ploy-ui/form-fields";
export const GroupInformation = ({ customerId, limitCustomers, addLimitCustomers, customerSearchPending, disabled }) => {
    const [open, setOpen] = useState(false);
    const [saving, setSaving] = useState(false);
    const [organizations, setOrganizations] = useState([]);
    const classes = useModalStyles();
    const theme = useTheme();
    const { data: customerGroup, loading } = useStatefulResource(CustomerGroupResource.detail(), (customerId ?? 0) > 0
        ? {
            customerId: customerId
        }
        : null);
    useEffect(() => {
        if (!loading && customerGroup?.organizations !== undefined)
            setOrganizations(customerGroup.organizations.map(o => ({
                ...o,
                addToLimitCustomers: false
            })));
    }, [customerGroup, loading, setOrganizations]);
    const updateGroup = useFetcher(CustomerGroupResource.update());
    const isRowMatch = (row, g) => {
        return (g.customerNumber === row.customerNumber &&
            g.customerName === row.customerName);
    };
    const columns = [
        {
            title: (_jsx(FormattedMessage, { id: `limit-customers-section.addToLimitCustomers`, description: "Add to LimitCustomers", defaultMessage: "Legg til som rammekunde" })),
            field: "addToLimitCustomers",
            render: row => {
                return limitCustomers.some(c => c.customerName === row.customerName) ? null : (_jsx(Checkbox, { checked: limitCustomers.some(c => isRowMatch(row, c)) ||
                        row.addToLimitCustomers ||
                        false, disabled: !row.import && !row.exists, onChange: (e, checked) => {
                        const newGroup = organizations.map(g => {
                            if (isRowMatch(row, g))
                                g.addToLimitCustomers = checked;
                            return g;
                        });
                        setOrganizations(newGroup);
                    } }));
            }
        },
        {
            title: (_jsx(FormattedMessage, { id: `limit-customers-section.import`, description: "Import", defaultMessage: "Importer" })),
            width: 100,
            field: "import",
            render: row => (_jsx(Checkbox, { checked: row.exists || row.import || false, disabled: customerGroup?.organizations.some(g => isRowMatch(row, g) && g.exists), onChange: (e, checked) => {
                    const newGroup = organizations.map(g => {
                        if (isRowMatch(row, g)) {
                            g.import = checked;
                            if (!checked)
                                g.addToLimitCustomers = false;
                        }
                        return g;
                    });
                    setOrganizations(newGroup);
                } }))
        },
        {
            title: (_jsx(FormattedMessage, { id: `limit-customers-section.exclude`, description: "Exclude", defaultMessage: "Ekskluder" })),
            width: 100,
            field: "exclude",
            render: row => (_jsx(Checkbox, { checked: row.exclude || false, onChange: () => {
                    const newGroup = organizations.map(g => {
                        if (isRowMatch(row, g))
                            g.exclude = !g.exclude;
                        return g;
                    });
                    setOrganizations(newGroup);
                } }))
        },
        {
            title: (_jsx(FormattedMessage, { id: `limit-customers-section.customerCompanyNumber`, description: "Organization number (bisnode modal)", defaultMessage: "Organisasjonsnummer" })),
            field: "customerNumber"
        },
        {
            title: (_jsx(FormattedMessage, { id: `limit-customers-section.customerName`, description: "Organization name", defaultMessage: "Navn" })),
            field: "customerName"
        }
    ];
    return (_jsxs(_Fragment, { children: [_jsx(DployFormControl, { margin: "normal", children: _jsx(Button, { disabled: disabled, color: "secondary", style: { marginTop: -3 }, size: "large", variant: "outlined", onClick: () => setOpen(true), children: _jsx(Typography, { children: _jsx(FormattedMessage, { id: `limit-customers-section.customerGroup`, description: "Customer group options", defaultMessage: "Bisnode" }) }) }) }), _jsx(Modal, { "aria-labelledby": "customer-group-search-modal-title", "aria-describedby": "customer-group-modal-description", className: classes.modal, open: open, 
                //onClose={() => setOpen(false)}
                closeAfterTransition: true, BackdropComponent: Backdrop, BackdropProps: {
                    timeout: 500
                }, children: _jsx(Fade, { in: open, children: _jsxs(Card, { className: classes.card, children: [_jsx(CardHeader, { title: _jsx(Typography, { variant: "h5", children: _jsx(FormattedMessage, { id: `limit-customers-section.customer-goup.bisnodeModalTitle`, description: "Title of bisnode search modal", defaultMessage: "Konsern" }) }), action: _jsx(IconButton, { onClick: () => setOpen(false), "aria-label": "modal-close-label", children: _jsx(CloseIcon, {}) }) }), _jsx(CardContent, { children: loading || customerSearchPending ? (_jsx(CircularProgress, { color: "inherit", size: 20 })) : (_jsxs(Suspense, { fallback: null, children: [_jsx(DployTable, { data: organizations, columns: columns, options: {
                                                headerStyle: {
                                                    background: theme.palette.secondary.main,
                                                    color: theme.palette.secondary.contrastText
                                                },
                                                search: false,
                                                header: true,
                                                toolbar: false,
                                                pageSize: 15,
                                                pageSizeOptions: [],
                                                idSynonym: "customerId"
                                            } }), _jsxs("div", { className: classes.buttonContainer, children: [_jsxs(Button, { className: classes.button, variant: "contained", color: "secondary", size: "small", onClick: () => {
                                                        setSaving(true);
                                                        updateGroup({}, {
                                                            customerId: customerId,
                                                            organizations: organizations
                                                        }).then(res => {
                                                            const newLimitCustomers = res.organizations
                                                                .filter(o => o.addToLimitCustomers && !!o.customerId)
                                                                .map(org => ({
                                                                customerId: org?.customerId,
                                                                customerName: org?.customerName || ""
                                                            }));
                                                            addLimitCustomers(newLimitCustomers);
                                                            setSaving(false);
                                                            setOpen(false);
                                                        });
                                                    }, children: [_jsx(Typography, { children: _jsx(FormattedMessage, { id: `limit-customers-section.customer-goup.save`, description: "Save customer group options", defaultMessage: "Lagre" }) }), saving ? (_jsx(CircularProgress, { color: "inherit", size: 20 })) : null] }), _jsx(Button, { className: classes.button, variant: "contained", color: "secondary", size: "small", onClick: () => setOpen(false), children: _jsx(Typography, { children: _jsx(FormattedMessage, { id: `limit-customers-section.customer-goup.close`, description: "Close customer group options", defaultMessage: "Lukk" }) }) })] })] })) })] }) }) })] }));
};
//# sourceMappingURL=GroupInformation.js.map